import React, { useState, useEffect, useMemo } from "react";
import { ugLibraryApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./UGLibrary.scss";

// ─── Constants ────────────────────────────────────────────────────────────────
const defaultInfo = {
  Page_Title: "",
  Subtitle: "",
  Description: "",
  Total_Books: "",
  Total_Journals: "",
  Seating_Capacity: "",
  Established_Year: "",
  Library_Area: "",
  Internet_Facility: "Available",
  Librarian_Name: "",
  Location: "",
  Librarian_Email: "",
  Librarian_Phone: "",
  OPAC_Link: ""
};

const UGLibrary = () => {
  const [formData, setFormData] = useState(defaultInfo);
  const [loading, setLoading]   = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await apiClient(`${ugLibraryApi}/page-info`);
      const result = await response.json();
      if (response.ok && result.data) {
        setFormData(result.data);
      }
    } catch (error) {
      console.error("Error fetching UG Library data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await apiClient(`${ugLibraryApi}/page-info`, {
        method: "POST",
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setIsEditing(false);
        fetchData();
      }
    } catch (error) {
      console.error("Error saving library info:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ── Derived Stats ──
  const stats = useMemo(() => [
    { label: "Total Books", val: formData.Total_Books || "0+", icon: "📚" },
    { label: "Journals", val: formData.Total_Journals || "0+", icon: "📰" },
    { label: "Seating", val: formData.Seating_Capacity || "0", icon: "🪑" },
    { label: "Est. Year", val: formData.Established_Year || "—", icon: "🏛️" }
  ], [formData]);

  return (
    <div className="ugl-page">
      {/* ── Header ── */}
      <div className="ugl-header">
        <div className="ugl-header-left">
          <div className="ugl-header-icon">📖</div>
          <div>
            <div className="ugl-breadcrumb">
              <span>Library</span>
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
              <span className="ugl-bc-active">UG Library</span>
            </div>
            <h1>{formData.Page_Title || "U.G. Library Management"}</h1>
            <p>{formData.Subtitle || "A rich repository of knowledge supporting academic excellence."}</p>
          </div>
        </div>
        <div className="ugl-header-actions">
           <button className="ugl-btn-primary" onClick={() => setIsEditing(true)}>
             <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" style={{marginRight:6}}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4L18.5 2.5z" /></svg>
             Edit Details
           </button>
        </div>
      </div>

      <div className="ugl-tabs">
        <button className={`ugl-tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>Overview</button>
        <button className={`ugl-tab ${activeTab === 'librarian' ? 'active' : ''}`} onClick={() => setActiveTab('librarian')}>Librarian & Contact</button>
        <button className={`ugl-tab ${activeTab === 'resources' ? 'active' : ''}`} onClick={() => setActiveTab('resources')}>Digital Resources</button>
      </div>

      {loading && <div className="loading-overlay">Syncing library records...</div>}

      <div className="ugl-tab-content">
        {activeTab === 'overview' && (
          <>
            <div className="ugl-preview-banner">
              <div className="upb-left">
                <span className="upb-badge">Academic Resource</span>
                <h2>Mission & Collection</h2>
                <p className="upb-desc">{formData.Description || "Providing a vast collection of books and journals to support undergraduate academic excellence."}</p>
                <div className="upb-contact-row">
                  <span>📍 {formData.Location || "Main Campus, Block A"}</span>
                  <span>📏 {formData.Library_Area || "5000 Sq. Ft."}</span>
                  <span>📶 WiFi: {formData.Internet_Facility || "Available"}</span>
                </div>
              </div>
              <div className="upb-right">
                <div className="upb-stats-grid">
                  <div><strong>{formData.Total_Books || "0"}</strong><span>Books</span></div>
                  <div><strong>{formData.Total_Journals || "0"}</strong><span>Journals</span></div>
                </div>
              </div>
            </div>

            <div className="ugl-overview-stats">
              {stats.map((s, i) => (
                <div key={i} className="ugl-stat-card">
                  <span className="ugl-stat-icon">{s.icon}</span>
                  <div>
                    <strong>{s.val}</strong>
                    <p>{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="ugl-notice-box">
              <span>🔔</span>
              <div>
                <strong>Library Policy</strong>
                <p>All members are expected to maintain silence and follow the borrowing guidelines strictly. ID cards are mandatory for entry and transactions.</p>
              </div>
            </div>
          </>
        )}

        {activeTab === 'librarian' && (
          <div className="ugl-info-row">
            <div className="ugl-info-box">
              <h3>Librarian Details</h3>
              <div className="ugl-info-list">
                <div className="ugl-info-row-item"><span>👤</span><p><strong>Name:</strong> {formData.Librarian_Name || "—"}</p></div>
                <div className="ugl-info-row-item"><span>📧</span><p><strong>Email:</strong> {formData.Librarian_Email || "—"}</p></div>
                <div className="ugl-info-row-item"><span>📞</span><p><strong>Phone:</strong> {formData.Librarian_Phone || "—"}</p></div>
              </div>
            </div>
            <div className="ugl-info-box">
              <h3>Library Access</h3>
              <div className="ugl-info-list">
                <div className="ugl-info-row-item"><span>🏛️</span><p><strong>Floor:</strong> {formData.Location || "—"}</p></div>
                <div className="ugl-info-row-item"><span>📅</span><p><strong>Working Days:</strong> Mon - Sat (09:00 AM - 05:00 PM)</p></div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'resources' && (
          <div className="ugl-info-box">
            <h3>Online Access & OPAC</h3>
            <div className="ugl-link-list">
               <div className="ugl-link-item">
                 <span>🔗</span>
                 <div>
                   <strong>OPAC Gateway</strong>
                   <p>{formData.OPAC_Link || "Access our digital catalog online to reserve books and check availability."}</p>
                 </div>
               </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Edit Modal ── */}
      {isEditing && (
        <div className="ugl-overlay" onClick={() => setIsEditing(false)}>
          <div className="ugl-form-modal" onClick={e => e.stopPropagation()}>
            <div className="ugl-modal-head">
              <div>
                <h2>Edit Library Details</h2>
                <p>Update statistical data and administrative information.</p>
              </div>
              <button className="ugl-modal-close" onClick={() => setIsEditing(false)}>×</button>
            </div>
            <form className="ugl-form" onSubmit={handleSave}>
              <div className="ugl-field full"><label>Page Title</label><input name="Page_Title" value={formData.Page_Title || ""} onChange={handleChange} required /></div>
              <div className="ugl-field full"><label>Subtitle</label><input name="Subtitle" value={formData.Subtitle || ""} onChange={handleChange} /></div>
              <div className="ugl-field full"><label>Description</label><textarea name="Description" value={formData.Description || ""} onChange={handleChange} rows="3" /></div>
              
              <div className="ugl-field-row">
                <div className="ugl-field"><label>Total Books</label><input name="Total_Books" value={formData.Total_Books || ""} onChange={handleChange} /></div>
                <div className="ugl-field"><label>Total Journals</label><input name="Total_Journals" value={formData.Total_Journals || ""} onChange={handleChange} /></div>
              </div>
              
              <div className="ugl-field-row">
                <div className="ugl-field"><label>Seating Capacity</label><input name="Seating_Capacity" value={formData.Seating_Capacity || ""} onChange={handleChange} /></div>
                <div className="ugl-field"><label>Established Year</label><input name="Established_Year" value={formData.Established_Year || ""} onChange={handleChange} /></div>
              </div>

              <div className="ugl-field-row">
                <div className="ugl-field"><label>Library Area (Sq Ft)</label><input name="Library_Area" value={formData.Library_Area || ""} onChange={handleChange} /></div>
                <div className="ugl-field"><label>Internet Facility</label><input name="Internet_Facility" value={formData.Internet_Facility || ""} onChange={handleChange} /></div>
              </div>

              <div className="ugl-field full"><label>Librarian Name</label><input name="Librarian_Name" value={formData.Librarian_Name || ""} onChange={handleChange} /></div>
              
              <div className="ugl-field-row">
                <div className="ugl-field"><label>Librarian Email</label><input type="email" name="Librarian_Email" value={formData.Librarian_Email || ""} onChange={handleChange} /></div>
                <div className="ugl-field"><label>Librarian Phone</label><input name="Librarian_Phone" value={formData.Librarian_Phone || ""} onChange={handleChange} /></div>
              </div>

              <div className="ugl-field full"><label>Physical Location</label><input name="Location" value={formData.Location || ""} onChange={handleChange} /></div>
              <div className="ugl-field full"><label>OPAC Link (Optional)</label><input name="OPAC_Link" value={formData.OPAC_Link || ""} onChange={handleChange} /></div>

              <div className="ugl-form-footer">
                <button type="button" className="ugl-btn-light" onClick={() => setIsEditing(false)}>Cancel</button>
                <button type="submit" className="ugl-btn-primary">Save Library Data</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UGLibrary;